// Seitenleiste im Dienstleistermodus (Handoff Dienstleister §9): Name, Avatar-Ton und Zähler je Bereich.
// Kern wie die Detailseite, sonst nur schmale Abfragen bzw. Zählabfragen (head).

import { useEffect, useState } from 'react';
import { supabase } from '../supabase/client';
import { heuteIso } from '../immobilien/format';
import { istVertrag, zuordnungLaeuft } from './liste';
import { nachweisStatus, aktuelleNachweise } from './nachweise';
import { istOffeneStufe, ticketStufe } from './detail';
import { DL_GEAENDERT } from './useDienstleisterListe';
import { ladeDlKern } from './useDienstleisterDetail';
import type { DlNavKey } from '../../components/immobilien/dienstleisterWidgets';

export interface DlKopf {
  name: string;
  untertitel: string;
  ton: 'grau' | 'bad' | 'acc' | 'info';
  anzahl: Partial<Record<DlNavKey, number | null>>;
  warn: Partial<Record<DlNavKey, boolean>>;
}
export type DlKopfZustand = { status: 'laedt' } | { status: 'fehlt' } | { status: 'fehler' } | { status: 'ok'; kopf: DlKopf };

const zahl = (r: { count: number | null; error: unknown }): number | null => (r.error ? null : r.count ?? 0);

export function useDienstleisterKopf(id: string | null): DlKopfZustand {
  const [zustand, setZustand] = useState<DlKopfZustand>({ status: 'laedt' });
  const [runde, setRunde] = useState(0);

  useEffect(() => {
    if (!id) return;
    let aktiv = true;
    setZustand({ status: 'laedt' });
    (async () => {
      const kern = await ladeDlKern(id);
      if (!aktiv) return;
      if (!kern) { setZustand({ status: 'fehlt' }); return; }
      const heute = heuteIso();
      const z = (tabelle: string) => supabase.from(tabelle).select('id', { count: 'exact', head: true });
      const [ti, re, nw, ko, te, dok, no, rg] = await Promise.all([
        supabase.from('tickets').select('status').eq('dienstleister_id', id).is('geloescht_am', null),
        supabase.from('rechnungen').select('status').eq('dienstleister_id', id).or('richtung.is.null,richtung.eq.eingang'),
        supabase.from('dienstleister_nachweise').select('art, gueltig_bis').eq('dienstleister_id', id),
        z('dienstleister_kontakte').eq('dienstleister_id', id),
        z('termine').eq('dienstleister_id', id).gte('start_datum', heute),
        z('dokumente').eq('dienstleister_id', id).eq('is_deleted', false),
        z('kontakt_notizen').eq('quelle', 'dienstleister').eq('quelle_id', id),
        supabase.from('dienstleister_regeln').select('gewerk_id').not('gewerk_id', 'is', null),
      ]);
      if (!aktiv) return;
      const tickets = ti.error ? null : (ti.data ?? []) as { status: string | null }[];
      const rechnungen = re.error ? null : (re.data ?? []) as { status: string | null }[];
      const nachweise = nw.error ? null : aktuelleNachweise(((nw.data ?? []) as { art: string; gueltig_bis: string | null }[]).map(n => ({ ...n, dienstleister_id: id })))
        .map(n => nachweisStatus(n.gueltig_bis, heute));
      const regeln = rg.error ? null : ((rg.data ?? []) as { gewerk_id: string }[]).filter(r => kern.kategorieIds.includes(r.gewerk_id)).length;
      const zuordnungen = kern.zuordnungen.filter(o => zuordnungLaeuft(o, heute));
      const kats = kern.kategorien.filter(k => kern.kategorieIds.includes(k.id));
      const d = kern.dienstleister;
      setZustand({
        status: 'ok',
        kopf: {
          name: (d.name ?? '').trim() || 'Ohne Namen',
          untertitel: kats.map(k => k.name).join(', ') || 'Keine Kategorie',
          ton: d.aktiv === false ? 'grau' : d.ist_notfallkontakt ? 'bad' : kats.some(k => k.ist_handwerk) ? 'acc' : 'info',
          anzahl: {
            tickets: tickets ? tickets.filter(t => istOffeneStufe(ticketStufe(t.status))).length : null,
            rechnungen: rechnungen ? rechnungen.filter(r => (r.status ?? '').toLowerCase() !== 'bezahlt').length : null,
            objekte: new Set(zuordnungen.map(o => o.objekt_id)).size,
            kategorien: kats.length,
            nachweise: nachweise ? nachweise.filter(s => s !== 'gueltig').length : null,
            vertraege: zuordnungen.filter(o => istVertrag(o.vertragsart)).length,
            regeln, ansprech: zahl(ko), termine: zahl(te), dokumente: zahl(dok), notizen: zahl(no),
          },
          warn: {
            nachweise: !!nachweise?.includes('abgelaufen'),
            rechnungen: !!rechnungen?.some(r => (r.status ?? '').toLowerCase() !== 'bezahlt'),
            tickets: !!tickets?.some(t => (t.status ?? '').toLowerCase() === 'neu'),
          },
        },
      });
    })().catch(() => { if (aktiv) setZustand({ status: 'fehler' }); });
    return () => { aktiv = false; };
  }, [id, runde]);

  useEffect(() => {
    const hoeren = (e: Event) => { const d = (e as CustomEvent<string | null>).detail; if (!d || d === id) setRunde(r => r + 1); };
    window.addEventListener(DL_GEAENDERT, hoeren);
    return () => window.removeEventListener(DL_GEAENDERT, hoeren);
  }, [id]);

  return zustand;
}
